import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNotification } from '../providers/NotificationProvider';
import { ChecklistTemplateVersion } from '../types';
import {
  TemplateVersionsApi,
  TemplateVersionResponse,
} from '../api-generated';
import { apiConfig } from '../api-generated/apiConfig';
import { isConflictError, getErrorMessage } from '../utils/apiErrorUtils';

const versionsApi = new TemplateVersionsApi(apiConfig);

/**
 * Information about a conflict detected during a save operation.
 */
export interface ConflictInfo {
  /** The sections the user was trying to save (unsaved changes) */
  unsavedSections: ChecklistTemplateVersion['sections'];
  /** Human-readable message about the conflict */
  message: string;
}

/**
 * Return type for the useTemplateVersions hook.
 */
export interface UseTemplateVersionsResult {
  /** Version history ordered by versionNumber descending */
  versionHistory: ChecklistTemplateVersion[];
  /** Whether version history is loading */
  isLoadingHistory: boolean;
  /** Current DRAFT version (if exists) */
  draft: ChecklistTemplateVersion | undefined;
  /** Whether the draft query is loading */
  isLoadingDraft: boolean;
  /** Active (PUBLISHED) version (if exists) */
  activeVersion: ChecklistTemplateVersion | undefined;
  /** Whether the active version query is loading */
  isLoadingActive: boolean;
  /** Saves the draft with conflict detection */
  saveDraftVersion: (draft: ChecklistTemplateVersion) => void;
  /** Whether a save is in progress */
  isSaving: boolean;
  /** Publishes the current draft with a changelog */
  publishDraftVersion: (changelog: string) => void;
  /** Whether a publish operation is in progress */
  isPublishing: boolean;
  /** Rolls back to an archived version (creates new DRAFT from it) */
  rollback: (versionId: string) => void;
  /** Whether a rollback operation is in progress */
  isRollingBack: boolean;
  /** Conflict info if a 409 was detected on save */
  conflictInfo: ConflictInfo | null;
  /** Clears the conflict state and reloads the draft */
  resolveConflict: () => void;
  /** Refetch all version-related data */
  refetchAll: () => void;
}

/**
 * Hook that wraps TanStack Query for all template version API calls.
 *
 * Manages:
 * - Version history listing
 * - Draft loading
 * - Active version loading
 * - Save draft with 409 conflict detection
 * - Publish draft
 * - Rollback to archived version
 * - Loading states for all operations
 *
 * @param templateId - The template to manage versions for; queries are disabled if undefined
 */
export function useTemplateVersions(
  templateId: string | undefined
): UseTemplateVersionsResult {
  const queryClient = useQueryClient();
  const notification = useNotification();

  // ─── State for conflict handling ─────────────────────────────────────

  const [conflictInfo, setConflictInfo] = React.useState<ConflictInfo | null>(null);

  // ─── Queries ─────────────────────────────────────────────────────────

  const historyQuery = useQuery<ChecklistTemplateVersion[], Error>({
    queryKey: ['templateVersions', templateId, 'history'],
    queryFn: async () => {
      // The generated getVersionHistory() types the Flux response as a single object and its
      // deserializer collapses the JSON array — read the raw JSON to get the real array.
      // History returns FULL version content (sections), so draft/active/latest are derived from
      // it client-side — no need for separate getDraft/getActiveVersion round-trips.
      const response = await versionsApi.getVersionHistoryRaw({ templateId: templateId! });
      const json = await response.raw.json();
      return (Array.isArray(json) ? json : []) as ChecklistTemplateVersion[];
    },
    enabled: !!templateId,
  });

  const versions = historyQuery.data ?? [];
  const draftVersion = versions.find((v: ChecklistTemplateVersion) => v.status === 'DRAFT');
  const publishedVersion = versions.find(
    (v: ChecklistTemplateVersion) => v.status === 'PUBLISHED',
  );
  // Latest by version number — used as the editing seed when there is no draft yet.
  const latestVersion = [...versions].sort(
    (a: ChecklistTemplateVersion, b: ChecklistTemplateVersion) =>
      (b.versionNumber ?? 0) - (a.versionNumber ?? 0),
  )[0];


  // ─── Mutations ───────────────────────────────────────────────────────

  const saveMutation = useMutation<ChecklistTemplateVersion, Error, ChecklistTemplateVersion>({
    mutationFn: async (draft: ChecklistTemplateVersion) => {
      const result = await versionsApi.saveDraft({
        templateId: templateId!,
        saveDraftRequest: draft as any,
      });
      return result as unknown as ChecklistTemplateVersion;
    },
    onSuccess: () => {
      setConflictInfo(null);
      queryClient.invalidateQueries({ queryKey: ['templateVersions', templateId] });
      notification.showSuccess('Draft saved successfully.');
    },
    onError: (error: Error, variables) => {
      if (isConflictError(error)) {
        setConflictInfo({
          unsavedSections: variables.sections,
          message:
            'Another user has modified this draft. Your unsaved changes are shown below. Please reload to get the latest version.',
        });
        notification.showWarning(
          'Conflict detected: Another user has modified this draft. Please reload.'
        );
      } else {
        notification.showError(`Failed to save draft: ${getErrorMessage(error)}`);
      }
    },
  });

  const publishMutation = useMutation<ChecklistTemplateVersion, Error, string>({
    mutationFn: async (changelog: string) => {
      const result = await versionsApi.publishDraft({
        templateId: templateId!,
        publishDraftRequest: { changelog },
      });
      return result as unknown as ChecklistTemplateVersion;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templateVersions', templateId] });
      notification.showSuccess('Version published successfully.');
    },
    onError: (error: Error) => {
      notification.showError(`Failed to publish: ${getErrorMessage(error)}`);
    },
  });

  const rollbackMutation = useMutation<ChecklistTemplateVersion, Error, string>({
    mutationFn: async (versionId: string) => {
      const result = await versionsApi.rollback({
        templateId: templateId!,
        rollbackRequest: { versionId },
      });
      return result as unknown as ChecklistTemplateVersion;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templateVersions', templateId] });
      notification.showSuccess('Rollback successful. A new draft has been created.');
    },
    onError: (error: Error) => {
      notification.showError(`Failed to rollback: ${getErrorMessage(error)}`);
    },
  });

  // ─── Actions ─────────────────────────────────────────────────────────

  const resolveConflict = () => {
    setConflictInfo(null);
    queryClient.invalidateQueries({ queryKey: ['templateVersions', templateId] });
  };

  const refetchAll = () => {
    queryClient.invalidateQueries({ queryKey: ['templateVersions', templateId] });
  };

  return {
    versionHistory: versions,
    isLoadingHistory: historyQuery.isLoading,
    // Editing seed: the persisted DRAFT if one exists, otherwise the latest version's content
    // (so a published template still opens with its content — no draft is created just to view).
    draft: draftVersion ?? latestVersion ?? undefined,
    isLoadingDraft: historyQuery.isLoading,
    activeVersion: publishedVersion ?? undefined,
    isLoadingActive: historyQuery.isLoading,
    saveDraftVersion: (draft) => saveMutation.mutate(draft),
    isSaving: saveMutation.isPending,
    publishDraftVersion: (changelog) => publishMutation.mutate(changelog),
    isPublishing: publishMutation.isPending,
    rollback: (versionId) => rollbackMutation.mutate(versionId),
    isRollingBack: rollbackMutation.isPending,
    conflictInfo,
    resolveConflict,
    refetchAll,
  };
}
